<template>
    <div v-if="!isAutoProvisionedTrialExpired">
        <pendo-popover-v2
            ref="settingsPopover"
            :title="$t('settings.Settings title')"
            placement="bottom-end"
            :hover="false"
            :popper-class="['settings__popover', 'top-nav__popover']"
            keyboard-navigation
            @show="focusFirstOption"
            @hide="$emit('select')">
            <template #default="{ shown, ...triggerAttrs }">
                <button
                    v-pendo-tooltip="{
                        content: $t('settings.Settings title'),
                        arrow: true,
                    }"
                    v-bind="triggerAttrs"
                    data-cy="settings-dropdown-trigger"
                    pendo-analytics="settings-nav-item"
                    class="top-nav__trigger top-nav__button-trigger"
                    :class="{
                        'is-open': shown,
                    }"
                    :aria-label="$t('settings.Settings title')"
                    @keydown.tab="handleTabKeydown($event, shown, triggerAttrs)">
                    <settings-icon />
                </button>
            </template>
            <template #popover>
                <div
                    ref="settingsContent"
                    class="top-nav__popover-content"
                    data-cy="settings-dropdown-content"
                    role="menu">
                    <h2
                        class="nav-title"
                        role="presentation">
                        {{ $t('settings.Settings title') }}
                    </h2>
                    <pendo-router-link
                        v-for="option in filteredOptions"
                        :key="option.id"
                        tabindex="0"
                        class="nav-option"
                        role="menuitem"
                        :aria-label="option.label"
                        :data-cy="`settings-dropdown-option-${option.id}`"
                        :pendo-analytics="option.pendoAnalytics"
                        :to="option.route"
                        @click="handleOptionClick($event, option)"
                        @keydown.space="handleOptionKeydown($event, option)"
                        @keydown.enter="handleOptionKeydown($event, option)"
                        @keydown.tab.prevent="handleOptionKeydown($event, option)">
                        <div
                            v-pendo-tooltip="{
                                content: tooltipContent,
                                arrow: true,
                                placement: 'top',
                                delay: { show: 1000, hide: 0 },
                            }"
                            class="nav-option__text"
                            @mouseover="onMouseover($event, option)">
                            {{ option.label }}
                        </div>
                        <component
                            :is="option.tag.type"
                            v-if="showTag(option)"
                            :size="option.tag.size || 'medium'"
                            class="nav-option__tag" />
                    </pendo-router-link>
                </div>
            </template>
        </pendo-popover-v2>
        <pendo-modal
            height="auto"
            title="Select an app"
            :visible="showAppChooserModal"
            :action-buttons="appChooserModalActionButtons"
            @close="onCloseAppChooserModal">
            <local-app-chooser
                v-model="selectedMobileAppId"
                append-to-body
                exclude-groups
                :labels-config="{ top: $t('launchAppModal.Select mobile app') }"
                placeholder="Select mobile app"
                filter-non-integrated-apps
                :show-when-single-app="true"
                :platform="mobilePlatforms" />
        </pendo-modal>
    </div>
</template>

<script>
import { adminRedirect } from '@/vue-router/router';
import { PendoTooltip, PendoPopoverV2, PendoModal } from '@pendo/components-vue3';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import SettingsIcon from '@/top-nav/components/icons/settings-icon.vue';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import {
    hasBasicOrgAdminRedirect,
    hasOEMSettingsRedirect,
    hasOEMSettingsApplicationRedirect,
    hasThemesRedirect,
    hasDataManagementRedirect,
    hasIntegrationsRedirect,
    DEFAULT_PERMISSIONS_REDIRECT,
} from '@/vue-router/route-permissions.utils';
import { adminRoutesGuard, excludeListRouteGuard, stagingRouteGuard } from '@/vue-router/route-guards.utils.js';
import { mapGetters } from 'vuex';
import { mapState as mapPiniaState } from 'pinia';
import { useOrganizationStore } from '@/stores/organization.store';
import LocalAppChooser from '@/common/app-chooser/local/local-app-chooser.vue';
import { startMobileDebugger } from '@utils/apps';
import { AppsService } from '@/common/apps/apps.service';
import isEmpty from 'lodash/isEmpty';
import { elementHasOverflowX } from '@/utils/dom.utils';
import UpsellTag from '@/common/tags/upsell-tag.vue';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

export default {
    name: 'SettingsDropdown',
    components: {
        PendoPopoverV2,
        PendoModal,
        PendoRouterLink,
        SettingsIcon,
        LocalAppChooser,
        UpsellTag,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
    data () {
        return {
            showAppChooserModal: false,
            selectedMobileAppId: null,
            mobilePlatforms: AppsService.MOBILE_PLATFORMS,
            tooltipContent: null,
        };
    },
    computed: {
        ...mapGetters({
            showTeams: 'teams/showTeams',
            mobileApps: 'apps/integratedMobileList',
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
            isInTrial: 'subscription/isInTrial',
            isFreeOrStarter: 'subscription/isFreeOrStarter',
            isAdoptEndUser: 'subscription/isAdoptEndUser',
        }),
        ...mapPiniaState(useOrganizationStore, { activeOrganization: 'getActive' }),
        settingsOptions () {
            return [
                {
                    id: 'organization-settings',
                    label: this.$t('settings.Organization settings'),
                    route: { name: 'OrgAdmin' },
                    pendoAnalytics: 'organization-settings',
                    hasPermissions: () =>
                        !hasBasicOrgAdminRedirect({
                            isAEU: this.isAdoptEndUser,
                        }) && !isEmpty(this.activeOrganization),
                },
                {
                    id: 'subscription-settings',
                    label: this.$t('settings.Subscription settings'),
                    route: { name: 'Admin' },
                    pendoAnalytics: 'subscription-settings',
                    hasPermissions: () => {
                        const { name } = adminRedirect({});

                        return name !== DEFAULT_PERMISSIONS_REDIRECT.name;
                    },
                },
                {
                    id: 'oem-settings',
                    label: this.$t('settings.Settings title'),
                    route: { name: 'OemSettings' },
                    pendoAnalytics: 'oem-settings',
                    hasPermissions: () => !hasOEMSettingsApplicationRedirect(),
                },
                {
                    id: 'adopt-settings',
                    label: this.$t('settings.OEM settings'),
                    route: { name: 'AdoptV2Settings' },
                    pendoAnalytics: 'adopt-settings',
                    hasPermissions: () => !hasOEMSettingsRedirect(),
                },
                {
                    id: 'themes',
                    label: this.$t('settings.Themes'),
                    route: { name: 'Themes' },
                    pendoAnalytics: 'themes',
                    hasPermissions: () => !hasThemesRedirect(),
                },
                {
                    id: 'metadata',
                    label: this.$t('settings.Metadata'),
                    route: { name: 'DataMappings' },
                    pendoAnalytics: 'data-mappings',
                    hasPermissions: () => !adminRoutesGuard(),
                },
                {
                    id: 'event-properties',
                    label: this.$t('settings.Event properties'),
                    route: { name: 'EventProperties' },
                    pendoAnalytics: 'event-properties',
                    hasPermissions: () => !adminRoutesGuard(),
                },
                {
                    id: 'data-sync',
                    label: this.$t('settings.Data Sync'),
                    route: { name: 'DataManagement' },
                    pendoAnalytics: 'data-sync',
                    hasPermissions: () => !hasDataManagementRedirect(),
                    tag: {
                        type: 'UpsellTag',
                    },
                },
                {
                    id: 'users',
                    label: this.showTeams ? this.$t('settings.Users and teams') : this.$t('settings.Users'),
                    route: { name: 'UsersAdmin' },
                    pendoAnalytics: 'users',
                    hasPermissions: () => !adminRoutesGuard({ isAdoptOnCoreAllowed: true }),
                },
                {
                    id: 'exclude-include',
                    label: this.$t('settings.Exclude and include lists'),
                    route: { name: 'Blacklist' },
                    pendoAnalytics: 'exclude-include',
                    hasPermissions: () => !excludeListRouteGuard(),
                },
                {
                    id: 'staging',
                    label: this.$t('settings.Staging'),
                    route: { name: 'Staging' },
                    pendoAnalytics: 'staging',
                    hasPermissions: () => !stagingRouteGuard(),
                },
                {
                    id: 'integrations',
                    label: this.$t('settings.Integrations'),
                    route: { name: 'Integrations' },
                    pendoAnalytics: 'integrations',
                    hasPermissions: () => !hasIntegrationsRedirect(),
                    tag: {
                        type: 'UpsellTag',
                    },
                },
                {
                    id: 'error-log',
                    label: this.$t('settings.Error log'),
                    route: { name: 'ErrorlogAdmin' },
                    pendoAnalytics: 'error-log',
                    hasPermissions: () => !adminRoutesGuard(),
                },
                {
                    id: 'mobile-debugger',
                    label: this.$t('settings.Mobile debugger'),
                    pendoAnalytics: 'mobile-debugger',
                    hasPermissions: () => this.hasIntegratedMobileApps && !this.$productLine(PRODUCT_LINES.ADOPT),
                    onClick: () => {
                        this.showAppChooserModal = true;
                    },
                },
                {
                    id: 'user-profile',
                    label: this.$t('settings.User profile'),
                    route: { name: 'UserProfile' },
                    pendoAnalytics: 'user-profile',
                },
            ];
        },
        filteredOptions () {
            return this.settingsOptions.filter((option) => {
                if ((option.tag?.type === 'UpsellTag' && this.isFreeOrStarter) || !option.hasPermissions) {
                    return true;
                }

                return option.hasPermissions();
            });
        },
        hasIntegratedMobileApps () {
            return this.mobileApps.length > 0;
        },
        appChooserModalActionButtons () {
            return [
                {
                    type: 'secondary',
                    label: 'Cancel',
                    theme: 'app',
                    handler: this.onCloseAppChooserModal,
                },
                {
                    type: 'primary',
                    label: 'Next',
                    theme: 'app',
                    disabled: this.selectedMobileAppId === null,
                    handler: this.onConfirmAppChooserModal,
                },
            ];
        },
    },
    methods: {
        handleOptionClick ($event, option) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            if (option.onClick) {
                option.onClick();
            }

            this.hideAndEmitSelect();
        },
        handleOptionKeydown ($event, option) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();

            if (option.onClick) {
                option.onClick();
                this.hideAndEmitSelect();

                return;
            }

            $event.currentTarget.click();
        },
        hide () {
            this.$refs.settingsPopover?.hide?.();
        },
        hideAndEmitSelect () {
            this.hide();
            this.$emit('select');
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.settingsContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        onCloseAppChooserModal () {
            this.showAppChooserModal = false;
            this.selectedMobileAppId = null;
        },
        onConfirmAppChooserModal () {
            const appId = this.selectedMobileAppId;
            this.onCloseAppChooserModal();
            startMobileDebugger(appId);
        },
        onMouseover ({ currentTarget }, option) {
            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = option.label;
            } else {
                this.tooltipContent = null;
            }
        },
        showTag (option) {
            // integrations specifically should still show a tag if the user is in a free trial
            if (option.id === 'integrations' && this.isInTrial) {
                return true;
            }

            return (
                (option.tag?.type === 'UpsellTag' && !this.isInTrial && !option.hasPermissions?.()) ||
                (!!option.tag && option.tag?.type !== 'UpsellTag')
            );
        },
        handleTabKeydown ($event, shown, triggerAttrs) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                if (shown) {
                    triggerAttrs.hide();
                } else {
                    triggerAttrs.show();
                }
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

:deep(.settings-icon) {
    height: 24px;
    width: 24px;
}

:deep(.pendo-tag__prefix-icon) {
    margin: 0;
}
</style>

<style lang="less">
.top-nav__popover.settings__popover {
    .pendo-popover-v3__panel {
        width: 250px;
        padding: 0;
    }
}
</style>
